import { useEffect, useState, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import { Box, Container, Flex, Text, Button } from '@radix-ui/themes';
import { supabase, checkConnection, handleNetworkError } from './lib/supabase';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import { showAlert } from './lib/alerts';
import { WifiOffIcon, RefreshCwIcon, Loader2Icon } from 'lucide-react';
import type { Session } from '@supabase/supabase-js';

// Lazy loading delle pagine per migliorare le performance
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const EmployeeDashboard = lazy(() => import('./components/employee/Dashboard'));
const Profile = lazy(() => import('./pages/Profile'));
const Attendance = lazy(() => import('./pages/Attendance'));
const Users = lazy(() => import('./pages/Users'));
const Centers = lazy(() => import('./pages/Centers'));
const TabletSecure = lazy(() => import('./pages/TabletSecure'));
const SecureAttendance = lazy(() => import('./pages/SecureAttendance'));
const Notifications = lazy(() => import('./pages/Notifications'));
const ConnectionTest = lazy(() => import('./components/ConnectionTest'));
const LocationManagement = lazy(() => import('./components/admin/LocationManagement'));

// Componente di loading per Suspense
const PageLoader = () => (
  <Flex
    align="center"
    justify="center"
    style={{ minHeight: '200px' }}
  >
    <Loader2Icon className="animate-spin" size={32} />
  </Flex>
);

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const location = useLocation();

  const checkUserRole = async (userId: string, retryCount = 0) => {
    try {
      // Check connection before proceeding
      const isConnected = await checkConnection();
      if (!isConnected) {
        setConnectionError(true);
        return;
      }
      setConnectionError(false);

      const { data: employee, error } = await supabase
        .from('employees')
        .select('role')
        .eq('id', userId)
        .single();

      if (error) throw error;

      if (!employee) return;

      // Admin check based on role from database
      setIsAdmin(employee.role === 'AMM.');
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error checking user role:', error);
      const errorMessage = handleNetworkError(error);
      showAlert(errorMessage, 'error');
      
      if (retryCount < 3) {
        setTimeout(() => {
          checkUserRole(userId, retryCount + 1);
        }, 1000 * Math.pow(2, retryCount));
      } else {
        setConnectionError(true);
      }
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        checkUserRole(session.user.id);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user) {
        checkUserRole(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Reset isInitialLoad when location changes
  useEffect(() => {
    setIsInitialLoad(false);
  }, [location.pathname]);

  // Special case for tablet-secure and presenza routes - no auth required
  if (location.pathname === '/tablet-secure' || location.pathname === '/presenza') {
    return (
      <Suspense fallback={<PageLoader />}>
        {location.pathname === '/tablet-secure' ? <TabletSecure /> : <SecureAttendance />}
      </Suspense>
    );
  }

  if (connectionError) {
    return (
      <Container size="1">
        <Flex 
          direction="column" 
          align="center" 
          justify="center" 
          gap="4"
          style={{ minHeight: '100vh' }}
        >
          <WifiOffIcon size={48} />
          <Text align="center" size="3">
            Impossibile connettersi al server. Verifica la tua connessione internet.
          </Text>
          <Button 
            onClick={() => window.location.reload()}
            style={{ marginTop: '1rem' }}
          >
            <RefreshCwIcon size={16} />
            Riprova
          </Button>
        </Flex>
      </Container>
    );
  }

  if (loading) {
    return (
      <Flex 
        align="center" 
        justify="center" 
        style={{ minHeight: '100vh', backgroundColor: 'var(--gray-2)' }}
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </Flex>
    );
  }

  if (!session) {
    return (
      <Container>
        <Login />
      </Container>
    );
  }

  return (
    <Flex style={{ minHeight: '100vh', backgroundColor: 'var(--gray-2)' }}>
      <Sidebar isAdmin={isAdmin} />
      <Box style={{ flex: 1, padding: 'var(--space-6)', overflowY: 'auto' }}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route
              path="/"
              element={
                isInitialLoad ? (
                  <Navigate to="/" replace />
                ) : (
                  isAdmin ? <AdminDashboard /> : <EmployeeDashboard />
                )
              }
            />
            <Route path="/attendance" element={<Attendance />} />
            {isAdmin && <Route path="/users" element={<Users />} />}
            {isAdmin && <Route path="/admin/locations" element={<LocationManagement />} />}
            <Route path="/centers" element={<Centers />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/test-connection" element={<ConnectionTest />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Box>
    </Flex>
  );
}

export default App;